import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Search, Plus, BookOpen, ChevronLeft, Mic } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { DeepLink } from '../../shared/types/navigation'
import { SkeletonListItem } from '@tendbot/ui'
import { NotebookPageList, PageContentView, NotebookSearch } from './components'
import { RecordingSheet } from '../recording'
import { findNote } from './notebookTree'
import { useNotebookState } from './useNotebookState'
import { useSwipeBack } from '../../shared/hooks/useSwipeBack'

interface Props {
  deepLink?: DeepLink | null
  onDeepLinkConsumed?: () => void
  onBack?: () => void
}

export default function Notebook({ deepLink, onDeepLinkConsumed, onBack }: Props) {
  const { t } = useTranslation('notebook')
  const [searchOpen, setSearchOpen] = useState(false)
  const [recordingOpen, setRecordingOpen] = useState(false)

  const {
    data,
    loading,
    currentPageId,
    navStack,
    newPageId,
    autoExpandIds,
    getStableKey,
    navigateToPage,
    navigateBack,
    handleTogglePin,
    handleDeleteNote,
    handleUpdateNote,
    handleCreateChild,
    handleMoveNote,
    handleNewPage,
    loadData,
    flushSaves,
  } = useNotebookState()

  const swipeBackProps = useSwipeBack(navigateBack)

  // Load data on mount
  useEffect(() => { loadData() }, [loadData])

  // Handle deep links (e.g. "View in notebook" from chat)
  useEffect(() => {
    if (deepLink?.type === 'note' && deepLink.id && !loading) {
      navigateToPage(deepLink.id)
      onDeepLinkConsumed?.()
    }
  }, [deepLink, loading, onDeepLinkConsumed, navigateToPage])

  const handleOpenSearch = useCallback(() => {
    setSearchOpen(true)
  }, [])

  const handleSearchNavigate = useCallback((id: string) => {
    navigateToPage(id)
    setSearchOpen(false)
  }, [navigateToPage])

  const currentNote = currentPageId && data ? findNote(data.notes, currentPageId) : null

  if (loading) {
    return (
      <div className="h-full overflow-y-auto px-5 py-4">
        <div className="h-8 w-32 bg-charcoal/50 rounded animate-shimmer mb-6" />
        <div className="space-y-2">
          <SkeletonListItem />
          <SkeletonListItem />
          <SkeletonListItem />
          <SkeletonListItem />
          <SkeletonListItem />
        </div>
      </div>
    )
  }

  // Build the full page stack: navStack history + current page on top (mobile only)
  const pageStack = data
    ? [...navStack, ...(currentPageId ? [currentPageId] : [])]
    : []

  /* -- Sidebar content (shared between mobile root view and desktop left pane) -- */
  const sidebarContent = (
    <>
      {/* Back button (mobile sub-screen) */}
      {onBack && (
        <div className="flex items-center px-4 pt-3 lg:hidden">
          <button onClick={onBack} className="flex items-center gap-1 text-pearl/50 hover:text-pearl text-sm transition-colors">
            <ChevronLeft size={16} />
            {t('back', { defaultValue: 'Back' })}
          </button>
        </div>
      )}
      {/* Search bar + actions */}
      <div className="flex flex-col gap-2 px-4 py-3">
        <button
          onClick={handleOpenSearch}
          className="flex items-center gap-2 bg-charcoal/60 border border-white/10 rounded-xl px-3 py-2.5 text-pearl/60 text-sm hover:border-gold/20 transition-colors"
        >
          <Search size={14} />
          {t('searchPlaceholder')}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRecordingOpen(true)}
            className="flex-1 flex items-center justify-center gap-1.5 bg-slate-mid/50 border border-gold/20 text-ivory text-sm font-medium px-3 py-2 rounded-xl hover:bg-slate-mid/70 hover:border-gold/40 transition-colors"
            title={t('record')}
          >
            <Mic size={14} />
            {t('record')}
          </button>
          <button
            onClick={handleNewPage}
            className="flex-1 flex items-center justify-center gap-1.5 bg-gold text-midnight text-sm font-medium px-3 py-2 rounded-xl hover:bg-gold-light transition-colors"
          >
            <Plus size={14} />
            {t('new')}
          </button>
        </div>
      </div>

      {/* Empty state */}
      {data && data.notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-6">
          <div className="w-16 h-16 rounded-2xl bg-charcoal/50 border border-gold/10 flex items-center justify-center mb-4">
            <BookOpen size={28} className="text-gold/40" />
          </div>
          <h3 className="font-display text-lg text-ivory font-semibold mb-2">{t('emptyState.title')}</h3>
          <p className="text-pearl/60 text-sm text-center mb-6 max-w-[240px]">
            {t('emptyState.description')}
          </p>
          <button
            onClick={handleNewPage}
            className="flex items-center gap-1.5 bg-gold text-midnight text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gold-light transition-colors"
          >
            <Plus size={14} />
            {t('emptyState.createFirst')}
          </button>
        </div>
      ) : (
        <NotebookPageList
          notes={data?.notes ?? []}
          activePageId={currentPageId}
          autoExpandIds={autoExpandIds}
          onNavigate={navigateToPage}
          onCreateChild={handleCreateChild}
          onMoveNote={handleMoveNote}
        />
      )}
    </>
  )

  return (
    <div className="h-full flex flex-col lg:flex-row relative">
      {/* Search overlay */}
      <NotebookSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        notes={data?.notes ?? []}
        onNavigate={handleSearchNavigate}
      />

      {/* -- Desktop two-pane layout (lg+) -- */}

      {/* Left pane -- page sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:w-72 xl:w-80 lg:border-r lg:border-gold/10 lg:overflow-y-auto lg:flex-shrink-0">
        {sidebarContent}
      </div>

      {/* Right pane -- page content */}
      <div className="hidden lg:flex lg:flex-1 lg:flex-col lg:min-w-0">
        {currentNote ? (
          <PageContentView
            note={currentNote}
            allNotes={data?.notes ?? []}
            isNew={currentNote.id === newPageId}
            embedded
            onBack={navigateBack}
            onNavigate={navigateToPage}
            onTogglePin={handleTogglePin}
            onDelete={handleDeleteNote}
            onUpdateNote={handleUpdateNote}
            onCreateChild={handleCreateChild}
            onRecord={() => setRecordingOpen(true)}
            onRefresh={loadData}
            onFlushSaves={flushSaves}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-pearl/55">
            <BookOpen size={40} className="mb-3 text-pearl/15" />
            <p className="text-sm">{t('selectPage')}</p>
          </div>
        )}
      </div>

      {/* -- Mobile layout (below lg) -- */}

      {/* Root view -- always mounted to preserve scroll + expansion state */}
      <div className="flex-1 overflow-y-auto lg:hidden">
        {sidebarContent}
      </div>

      {/* Page views -- stacked modals, each layer slides up over the previous (mobile only) */}
      <AnimatePresence>
        {pageStack.map((pageId, index) => {
          const pageNote = data ? findNote(data.notes, pageId) : undefined
          if (!pageNote) return null
          return (
            <motion.div
              key={getStableKey(pageNote.id)}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute inset-0 bg-midnight lg:hidden"
              style={{ zIndex: 20 + index }}
              {...swipeBackProps}
            >
              <PageContentView
                note={pageNote}
                allNotes={data?.notes ?? []}
                isNew={pageNote.id === newPageId}
                onBack={navigateBack}
                onNavigate={navigateToPage}
                onTogglePin={handleTogglePin}
                onDelete={handleDeleteNote}
                onUpdateNote={handleUpdateNote}
                onCreateChild={handleCreateChild}
                onRecord={() => setRecordingOpen(true)}
                onRefresh={loadData}
            onFlushSaves={flushSaves}
              />
            </motion.div>
          )
        })}
      </AnimatePresence>

      {/* Recording bottom sheet */}
      <RecordingSheet
        isOpen={recordingOpen}
        onClose={() => setRecordingOpen(false)}
        onNavigateToNote={(noteId) => {
          setRecordingOpen(false)
          loadData()
          navigateToPage(noteId)
        }}
        linkedEntryId={currentPageId}
        linkedEntryTitle={currentNote?.title}
      />
    </div>
  )
}
